import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Building2, Pencil, X, Save } from "lucide-react";
import { guestAPI, cls, fmtINR } from "./guestApi";
import { NumberInput } from "@/components/ui/number-input";

export default function GuestCompanies() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const load = () => guestAPI.companies({ q: q || undefined }).then(setRows);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]);

  return (
    <div className="space-y-4" data-testid="guest-companies">
      <div className="flex items-center gap-3">
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search companies…" aria-label="Search companies"
               data-testid="guest-co-search"
               className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px] w-72 focus:border-hocp-navy outline-none"/>
        <button onClick={()=>setEditing({ __new: true })} data-testid="guest-co-new"
                className="ml-auto h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-semibold inline-flex items-center gap-2 hover:brightness-95">
          <Plus className="w-4 h-4"/> New Company
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {rows.length === 0
          ? <div className="p-12 text-center">
              <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-2"/>
              <div className="text-[13px] text-slate-500">No corporate accounts yet.</div>
            </div>
          : <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr><th className="text-left px-4 py-3">Company</th><th className="text-left px-4 py-3">GST</th>
                    <th className="text-left px-4 py-3">Contact</th>
                    <th className="text-right px-4 py-3">Credit Days</th>
                    <th className="text-right px-4 py-3">Credit Limit</th>
                    <th className="text-right px-4 py-3"></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(c => (
                  <tr key={c.id} data-testid={`guest-co-${c.id}`}
                      className="hover:bg-slate-50/60 cursor-pointer" onClick={()=>setEditing(c)}>
                    <td className="px-4 py-3 font-medium text-hocp-navy">{c.name}</td>
                    <td className="px-4 py-3 mono text-[12px]">{c.gst_number || "—"}</td>
                    <td className="px-4 py-3 text-[12px] text-slate-600">
                      {c.contact_name || "—"}<br/>
                      <span className="text-[11px] text-slate-500">{c.contact_email || c.contact_phone || ""}</span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{c.credit_days ?? 0}d</td>
                    <td className="px-4 py-3 text-right tabular-nums">{c.credit_limit != null ? fmtINR(c.credit_limit) : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <Pencil className="w-3.5 h-3.5 text-slate-400 inline"/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>}
      </div>
      {editing && <CompanyDialog c={editing} onClose={()=>setEditing(null)} onDone={()=>{ setEditing(null); load(); }}/>}
    </div>
  );
}

function CompanyDialog({ c, onClose, onDone }) {
  const isNew = c.__new;
  const [f, setF] = useState({
    name: c.name || "", gst_number: c.gst_number || "",
    contact_name: c.contact_name || "", contact_email: c.contact_email || "",
    contact_phone: c.contact_phone || "", credit_days: c.credit_days || 0,
    credit_limit: c.credit_limit ?? "", billing_instructions: c.billing_instructions || "",
    is_active: c.is_active !== false,
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const body = { ...f, credit_days: Number(f.credit_days) || 0,
                       credit_limit: f.credit_limit === "" ? null : Number(f.credit_limit) };
      if (isNew) await guestAPI.createCompany(body);
      else       await guestAPI.updateCompany(c.id, body);
      toast.success("Saved"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg" onClick={e=>e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy">{isNew ? "New Company" : c.name}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4 text-slate-500"/></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <label className="col-span-2 block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Company name <span className="text-rose-500">*</span></span>
            <input placeholder="e.g. Acme Corp" value={f.name} onChange={e=>setF({...f, name: e.target.value})} data-testid="guest-co-name" className={cls + " w-full"}/></label>
          <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">GST number</span>
            <input placeholder="e.g. 29ABCDE1234F1Z5" value={f.gst_number} onChange={e=>setF({...f, gst_number: e.target.value})} className={cls + " w-full"}/></label>
          <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Credit days</span>
            <NumberInput placeholder="e.g. 30" value={f.credit_days} onChange={e=>setF({...f, credit_days: e.target.value})} className={cls + " w-full"}/></label>
          <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Contact name</span>
            <input placeholder="e.g. Rahul Verma" value={f.contact_name} onChange={e=>setF({...f, contact_name: e.target.value})} className={cls + " w-full"}/></label>
          <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Contact email</span>
            <input placeholder="e.g. accounts@acme.com" value={f.contact_email} onChange={e=>setF({...f, contact_email: e.target.value})} className={cls + " w-full"}/></label>
          <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Contact phone</span>
            <input placeholder="e.g. +91 98xxxxxx99" value={f.contact_phone} onChange={e=>setF({...f, contact_phone: e.target.value})} className={cls + " w-full"}/></label>
          <label className="block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Credit limit (₹)</span>
            <NumberInput placeholder="e.g. 100000" value={f.credit_limit} onChange={e=>setF({...f, credit_limit: e.target.value})} className={cls + " w-full"}/></label>
          <label className="col-span-2 block"><span className="block text-[11.5px] font-medium text-slate-600 mb-1">Billing instructions</span>
            <textarea placeholder="e.g. Consolidated monthly invoice" rows={2} value={f.billing_instructions} onChange={e=>setF({...f, billing_instructions: e.target.value})} className={cls + " w-full h-auto py-2"}/></label>
        </div>
        <div className="px-5 py-3 bg-slate-50 flex justify-end gap-2 border-t border-slate-100">
          <button onClick={onClose} className="h-9 px-3.5 rounded-lg text-slate-600 text-[13px]">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="guest-co-save"
                  className="h-9 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-60">
            <Save className="w-4 h-4"/>{saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
